import React, { useEffect, useState } from 'react';
import './index.css';

import CustomerDetails from "../../models/customerDetails";
import type { IRoofAddress } from "../../react-app-env";
import InputError from "../../components/InputError";
import { fullpageApi } from "@fullpage/react-fullpage";
import { Turnstile } from "@marsidev/react-turnstile";
import { RECAPTCHA_SITE_KEY } from '../../config/config';
import ReactiveButton from 'reactive-button';
import ScrollUpBtn from "../../components/ScrollUpBtn";

interface P {
  sectionId?: string;
  activePageId: string;
  i18nMap: any;
  fullpageApi: fullpageApi;
  roofAddress: IRoofAddress;
  customerDetails: CustomerDetails;
  handleInputChange: any;
  isAddressSame: boolean;
  toggleAddressCheck: any;
  errors: Map<string, string>;
  addError: (key: string, message: string) => void;
  removeError: (key: string) => void;
  onSubmitData: any;
  selectedDraft: IRoofAddress,
  setCaptchaToken: any,
  captchaToken: any,
  isDataSubmitting: any,
  apiResponse: any;
  setApiResponse: any;
  disableFields: boolean

}

export default (
  {
    sectionId = 'step5',
    activePageId,
    i18nMap,
    fullpageApi,
    customerDetails,
    handleInputChange,
    isAddressSame,
    toggleAddressCheck,
    errors,
    addError,
    removeError,
    onSubmitData,
    selectedDraft,
    setCaptchaToken,
    captchaToken,
    isDataSubmitting,
    apiResponse,
    setApiResponse,
    disableFields
  }: P
) => {

  const [isFocused, setIsFocused] = useState(false);

  const setAllowScrolling = (allowScrolling: boolean): void => {
    console.log(`fullpageApi.setAllowScrolling called with: ${allowScrolling}`);
    //fullpageApi.setAllowScrolling(allowScrolling);
  };

  const validateEmail = () => {
    if (customerDetails.email?.length === 0) return;

    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (regex.test(customerDetails.email)) {
      removeError('email');
    } else {
      addError('email', i18nMap.invalidEmail);
    }
  };

  const validatePhoneNumber = () => {
    if (customerDetails.phone?.length === 0) return;

    const regex = /^\+?(\d{1,4})?[-.\s]?(\(?\d{1,4}\)?)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
    if (regex.test(customerDetails.phone)) {
      removeError('phone');
    } else {
      addError('phone', i18nMap.invalidPhone);
    }
  };

  const isActive: boolean = activePageId === sectionId;
  const [buttonState, setButtonState] = useState('idle');
  const [isWaiting, setIsWaiting] = useState(true);
  const [idleStateText, setIdleStateText] = useState(i18nMap.submit);
  const [apiError, setApiError] = useState<string>();
  useEffect(() => {
    if (apiResponse && !isWaiting) {
      if (apiResponse.success) {
        setIdleStateText(i18nMap.submissionComplete);
        setButtonState("success");
      } else {
        setButtonState("error");
        setApiResponse(null);
        setApiError(apiResponse?.tokenResponse && !apiResponse?.tokenResponse?.success ? i18nMap.apiInvalidCaptchaMessage : i18nMap.apiErrorMessage)
        setTimeout(() => {
          setButtonState("idle");
        }, 2000);
      }
    }
  }, [apiResponse, isWaiting]);

  useEffect(() => {
    if (isDataSubmitting) {
      setIdleStateText(i18nMap.submit);
      setApiError("");
      setIsWaiting(true);
      setButtonState('loading');
      setTimeout(() => {
        setIsWaiting(false);
      }, 2000);
    }

  }, [isDataSubmitting]);

  const buttonClass = buttonState === 'success'
    ? 'submitted-data'
    : buttonState === 'error'
      ? 'submittion-error'
      : 'btn-transparent';

  return (
    <div id={sectionId} className={`header header-facade section s4-header`}>
      <div className={`outer-step`}>
        <ScrollUpBtn onClick={() => fullpageApi.moveSectionUp()} label={i18nMap.backToOfferings} />
        <div className="sub-step-title">
          <div className="sub-step-title-left">
            <span className="roof-icon-label"><i className="fa fa-envelope-o" /></span>
            {i18nMap.yourLocation}
          </div>
        </div>
        <div className={`inner-step step-4`}>

          <div className={`contact-form non-selectable`}>
            <div className={`title-and-names-holder`}>
              <div
                className={`basic-input-holder ${disableFields && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.firstName}
                  className={`basic-input`}
                  name={`firstName`}
                  tabIndex={isActive ? 1 : -1}
                  value={customerDetails.firstName}
                  onChange={handleInputChange}
                  disabled={disableFields}
                />
                <div
                  className={`basic-input-label ${disableFields && "disabled-label"
                    }`}
                >
                  {i18nMap.firstName}
                </div>

                {errors.get("firstName") && (
                  <InputError error={errors.get("firstName")} />
                )}
              </div>

              <div
                className={`basic-input-holder ${disableFields && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.surname}
                  className={`basic-input`}
                  name={`surname`}
                  tabIndex={isActive ? 2 : -1}
                  value={customerDetails.surname}
                  onChange={handleInputChange}
                  disabled={disableFields}
                />
                <div
                  className={`basic-input-label ${disableFields && "disabled-label"
                    }`}
                >
                  {i18nMap.surname}
                </div>

                {errors.get("surname") && (
                  <InputError error={errors.get("surname")} />
                )}
              </div>
            </div>

            <div className={`email-and-phone-holder`}>
              <div
                className={`basic-input-holder ${disableFields && "disabled-field"
                  }`}
              >
                <input
                  type={"email"}
                  placeholder={i18nMap.email}
                  className={`basic-input`}
                  name={`email`}
                  tabIndex={isActive ? 3 : -1}
                  value={customerDetails.email}
                  onBlur={validateEmail}
                  onChange={handleInputChange}
                  disabled={disableFields}
                />
                <div
                  className={`basic-input-label ${disableFields && "disabled-label"
                    }`}
                >
                  {i18nMap.email}
                </div>

                {errors.get("email") && (
                  <InputError error={errors.get("email")} />
                )}
              </div>
              <div
                className={`basic-input-holder ${disableFields && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.phone}
                  className={`basic-input`}
                  name={`phone`}
                  tabIndex={isActive ? 4 : -1}
                  value={customerDetails.phone}
                  onBlur={validatePhoneNumber}
                  onChange={handleInputChange}
                  disabled={disableFields}
                />
                <div
                  className={`basic-input-label ${disableFields && "disabled-label"
                    }`}
                >
                  {i18nMap.phone}
                </div>

                {errors.get("phone") && (
                  <InputError error={errors.get("phone")} />
                )}
              </div>
            </div>

            {!selectedDraft.hasCustomRoof &&
              selectedDraft.searchAddress !== null && (
                <div className={`check-holder`}>
                  <div
                    className={`custom-checkbox custom-checkbox-${isAddressSame && "checked"
                      }`}
                    tabIndex={isActive ? 5 : -1}
                    onClick={toggleAddressCheck}
                  ></div>
                  <div className={`address-check`} onClick={toggleAddressCheck}>
                    {i18nMap.addressSameAsAbove}
                  </div>
                </div>
              )}

            <div className={`address-holder`}>
              <div
                style={{ width: "100%" }}
                className={`basic-input-holder ${(isAddressSame || disableFields) && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.street}
                  className={`basic-input address-input`}
                  name={`street`}
                  tabIndex={isActive ? 6 : -1}
                  data-group={`address`}
                  value={customerDetails.customerAddress.street}
                  onChange={handleInputChange}
                  disabled={isAddressSame || disableFields}
                />
                <div
                  className={`basic-input-label ${(isAddressSame || disableFields) && "disabled-label"
                    }`}
                >
                  {i18nMap.street}
                </div>

                {!isAddressSame && errors.get("street") && (
                  <InputError error={errors.get("street")} />
                )}
              </div>
            </div>

            <div className={`street-and-city-holder`}>
              <div
                className={`basic-input-holder ${(isAddressSame || disableFields) && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.postalCode}
                  className={`basic-input address-input`}
                  tabIndex={isActive ? 7 : -1}
                  name={`postalCode`}
                  data-group={`address`}
                  value={customerDetails.customerAddress.postalCode}
                  onChange={handleInputChange}
                  disabled={isAddressSame || disableFields}
                />
                <div
                  className={`basic-input-label ${(isAddressSame || disableFields) && "disabled-label"
                    }`}
                >
                  {i18nMap.postalCode}
                </div>

                {!isAddressSame && errors.get("postalCode") && (
                  <InputError error={errors.get("postalCode")} />
                )}
              </div>

              <div
                className={`basic-input-holder ${(isAddressSame || disableFields) && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.city}
                  className={`basic-input address-input`}
                  name={`city`}
                  tabIndex={isActive ? 8 : -1}
                  data-group={`address`}
                  value={customerDetails.customerAddress.city}
                  onChange={handleInputChange}
                  disabled={isAddressSame || disableFields}
                />
                <div
                  className={`basic-input-label ${(isAddressSame || disableFields) && "disabled-label"
                    }`}
                >
                  {i18nMap.city}
                </div>

                {!isAddressSame && errors.get("city") && (
                  <InputError error={errors.get("city")} />
                )}
              </div>
            </div>

            <div className={`address-holder`} style={{ paddingRight: "10px" }}>
              <div
                className={`basic-input-holder ${(isAddressSame || disableFields) && "disabled-field"
                  }`}
              >
                <input
                  placeholder={i18nMap.state}
                  className={`basic-input`}
                  tabIndex={isActive ? 9 : -1}
                  name={`state`}
                  data-group={`address`}
                  value={customerDetails.customerAddress.state}
                  onChange={handleInputChange}
                  disabled={isAddressSame || disableFields}
                />
                <div
                  className={`basic-input-label ${(isAddressSame || disableFields) && "disabled-label"
                    }`}
                >
                  {i18nMap.state}
                </div>

                {!isAddressSame && errors.get("state") && (
                  <InputError error={errors.get("state")} />
                )}
              </div>
            </div>

            <div className={`comments-holder`}>
              <div
                style={{ width: "100%" }}
                className={`basic-input-holder ${disableFields && "disabled-field"
                  }`}
              >
                <textarea
                  style={{ width: "100%" }}
                  rows={3}
                  placeholder={i18nMap.comments}
                  tabIndex={isActive ? 10 : -1}
                  className={`basic-input address-input base-cs custom-v-scroll`}
                  name={`comment`}
                  onFocus={() => {
                    setAllowScrolling(false);
                    setIsFocused(true);
                  }}
                  onBlur={(e) => {
                    setAllowScrolling(true);
                    setIsFocused(false);
                  }}
                  value={customerDetails.comment}
                  onChange={handleInputChange}
                  disabled={disableFields}
                />
                <div
                  className={`basic-input-label ${disableFields && "disabled-label"
                    }`}
                >
                  {i18nMap.comments}
                </div>
              </div>
            </div>

            <div className={`captcha-holder`} style={{ paddingRight: "10px" }}>
              <div className={`basic-captcha-holder`}>
                <Turnstile
                  siteKey={RECAPTCHA_SITE_KEY}
                  onSuccess={(t) => setCaptchaToken(t)}
                  options={{
                    theme: "light",
                    language: "en",
                    appearance: "interaction-only",
                  }}
                />
                {!captchaToken && errors.get("captchaToken") && (
                  <InputError error={errors.get("captchaToken")} />
                )}
              </div>
            </div>

            <ReactiveButton
              buttonState={buttonState}
              size="large"
              className={buttonClass}
              messageDuration={1800}
              idleText={idleStateText}
              loadingText={i18nMap.sendingData}
              errorText={i18nMap.submissionError}
              color="teal"
              disabled={buttonState === "success"}
              type="submit"
              successText={i18nMap.submissionComplete}
              onClick={() => {
                onSubmitData(fullpageApi);
              }}
              style={{
                cursor: disableFields ? "not-allowed" : "pointer",
                pointerEvents: isActive ? "unset" : "none",
              }}
            />
            {buttonState === "success" && (
              <div className="success-message">
                {
                  i18nMap.additionalSuccessMessage /* Ensure this key is defined in your i18nMap */
                }
              </div>
            )}
            {apiError && (
              <div className={`api-error-holder`}>
                <div className={`api-err-msg`}>{apiError}</div>
              </div>
            )}

            <div className="inline-imprint">
              <p>{i18nMap.consultantHint}</p>
              <p>
                © {new Date().getFullYear()}{' '}
                <a tabIndex={-1} href="https://elektrobedarf.ch" target="_blank" rel="noreferrer">Elektrobedarf Troller AG</a>
                {' | '}{i18nMap.publisher}
                {' | '}{i18nMap.development}{' '}
                <a tabIndex={-1} href="https://t2consult.de" target="_blank" rel="noreferrer">t2consult</a>
                {' - '}{i18nMap.t2consultAddress}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
